import { Box, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import AgentServicesTable, {
  getAgentTicketsPerServiceRows,
  renderAgentTicketsPerServiceCell,
} from "../tables/AgentServicesTable";
import {
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
  usesUnifiedTable,
} from "../../../store/renderConfig";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";
import EnterpriseReportSummary from "../../reports/EnterpriseReportSummary";
import { buildTicketVolumeInsights } from "../../../store/reportEnterpriseInsights";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";

const AGENT_SERVICE_COLUMN = Object.freeze({
  id: "agentService",
  label: "Agent / Service",
  pinned: true,
});

const AgentServices = ({ reportIndex, data, reportTitle, reportDescription, presentationSummaryItems = [] }) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const config = normalizeRenderConfig({ ...renderConfig, charts: { enabled: false } }, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const useUnifiedTable = usesUnifiedTable(config, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });

  return (
    <Box>
      {data.map(
        ({ branchName, servicesData, numServices }, branchIndex) => {
          const title = `${reportIndex || ""}.${
            branchIndex + 1
          }. ${branchName}`;
          const insights = buildTicketVolumeInsights(
            { branchName, servicesData },
            { scopeLabel: "Agent" }
          );
          return (
            <Box key={`${reportIndex}-${branchIndex}`}>
              {branchIndex === 0 ? (
                <>
                  <h1>{reportTitle}</h1>
                  <p>{reportDescription}</p>
                  {presentationSummaryItems.length ? (
                    <div className="reportPresentationSummary">
                      <span className="reportPresentationSummaryLabel">Presentation</span>
                      <span className="reportPresentationSummaryValue">
                        {presentationSummaryItems.join(" / ")}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : null}
              <h2>{title}</h2>
              <Text>{numServices} services.</Text>
              <EnterpriseReportSummary
                title="Agent Ticket Mix Summary"
                kpis={insights.kpis}
                tables={insights.tables}
                notes={insights.notes}
              />
              {useUnifiedTable ? (
                <Box className="reportTableContainer agentTicketsPerServiceUnifiedBlock">
                  <ConfiguredReportTable
                    pinnedColumns={[AGENT_SERVICE_COLUMN]}
                    columns={AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
                    rows={(servicesData || []).flatMap((service) =>
                      sortReportRows(getAgentTicketsPerServiceRows({ branchName }, service), config, {
                        columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
                      })
                    )}
                    renderConfig={config}
                    renderCell={(agentData, columnId) => {
                      if (columnId === "agentService") {
                        return (
                          <Box>
                            <Text>{agentData.agentName}</Text>
                            <Text className="agentServiceMutedCell">{agentData.serviceName}</Text>
                          </Box>
                        );
                      }
                      return renderAgentTicketsPerServiceCell(agentData, columnId);
                    }}
                    presortedRows={true}
                  />
                </Box>
              ) : (
                servicesData.map((data, index) => {
                return (
                  <AgentServicesTable
                    key={index}
                    index={index}
                    serviceData={data}
                    reportTitle={reportTitle}
                    reportDescription={reportDescription}
                    showReportTitle={false}
                    branchTitle={title}
                    numServices={numServices}
                    showBranchTitle={false}
                    branchIndex={branchIndex}
                    reportIndex={reportIndex}
                    branchName={branchName}
                  />
                );
              }))}
            </Box>
          );
        }
      )}
    </Box>
  );
};

export default AgentServices;
